import { useAction, useAtom } from '@reatom/npm-react'
import { throttle } from 'radash'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { CircularLoader, Eye1Icon, Eye2Icon } from '@blade/ui-kit'
import { PairPopover } from '@root/features/pair'
import { reatomPositionVisibilityToggle } from '@root/features/trade/position/visibility/atoms'

import { TopbarPairItem } from '../common/topbar-pair-item'

import type { AggregatedPositionModel } from '@root/entities/position'
import type { ClassNameProps } from '@root/shared/react'
import type { MouseEvent } from 'react'

type TopbarPositionProps = {
  position: AggregatedPositionModel
} & ClassNameProps

/** At most one display refresh per this interval (first update in a window is immediate). */
const DISPLAY_THROTTLE_MS = 5000

export const TopbarPosition = memo(
  ({ position, className }: TopbarPositionProps) => {
    const [pnl] = useAtom(position.unrealizedPnlUsd)
    const [remainingSizeUsd] = useAtom(position.remainingSizeUsd)

    const [display, setDisplay] = useState({
      remainingSizeUsd,
      percents: pnl.pnlPercentage.toNumber(),
    })

    const setDisplayThrottled = useMemo(
      () => throttle({ interval: DISPLAY_THROTTLE_MS }, setDisplay),
      [],
    )

    useEffect(() => {
      setDisplayThrottled({
        remainingSizeUsd,
        percents: pnl.pnlPercentage.toNumber(),
      })
    }, [pnl, remainingSizeUsd, setDisplayThrottled])

    return (
      <PairPopover pair={position.pair} sideOffset={2}>
        <TopbarPairItem
          className={className}
          key={position.pair.address}
          pair={position.pair}
          amountUsd={display.remainingSizeUsd}
          amountFormat="amount"
          percents={display.percents}
          actionsSlot={<PositionVisibilityToggler position={position} />}
        />
      </PairPopover>
    )
  },
)

const PositionVisibilityToggler = ({
  position,
}: {
  position: AggregatedPositionModel
}) => {
  const [hidden] = useAtom(position.hidden)
  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomPositionVisibilityToggle({
        pairAddress: position.pairAddress,
        network: position.network,
      }),
    [position.network, position.pairAddress],
  )
  const toggleVisibility = useAction(model.execute)
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsLoading(true)
      await toggleVisibility()
      setIsLoading(false)
    },
    [toggleVisibility],
  )

  const renderHiddenIcon = () => {
    return hidden ? <Eye1Icon size={16} /> : <Eye2Icon size={16} />
  }

  return (
    <Root
      type="button"
      onClick={handleClick}
      onPointerDownCapture={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onPointerUpCapture={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {isLoading ? <CircularLoader size={16} /> : renderHiddenIcon()}
    </Root>
  )
}

const Root = styled.button`
  all: unset;
  cursor: pointer;
  display: grid;
  gap: 8px;
  width: 23px;
  height: 100%;

  justify-content: center;
  align-content: center;
  border-inline-start: 1px solid var(--background-3);
`
